import { IntlShape, defineMessages } from 'react-intl'
import * as yup from 'yup'

import { INewPartnerStep2 } from 'features/Partners/types'
import { every, isEmpty, values } from 'lodash'
import schemaValidations from 'utils/schema'

const messages = defineMessages({
  required: {
    id: 'partnerData.new.form.required',
    defaultMessage: 'This field is required'
  },
  clabeLength: {
    id: 'partnerData.new.bankData.clabe.length',
    defaultMessage: 'Enter 18 digits'
  },
  maxLength: {
    id: 'partnerData.new.bankData.maxLength',
    defaultMessage: 'Enter max {nChar} characters'
  }
})

const base = (intl: IntlShape): yup.SchemaOf<INewPartnerStep2['bankData']> => {
  return yup.object().shape({
    email: schemaValidations(intl).email.optional,
    bankCoverLink: yup.string().max(255, intl.formatMessage(messages.maxLength, { nChar: 255 })),
    paymentType: yup.string(),
    bankName: yup.string(),
    clabeNumber: yup.string().length(18, intl.formatMessage(messages.clabeLength))
  }) as yup.SchemaOf<INewPartnerStep2['bankData']>
}

const isBankDataTouched = (data: INewPartnerStep2['bankData']): boolean => {
  return !every(values(data), isEmpty)
}

const optional = (intl: IntlShape): yup.SchemaOf<INewPartnerStep2['bankData']> => {
  return yup.object().shape({
    bankName: base(intl).fields.bankName.when('$bankData', {
      is: (data: INewPartnerStep2['bankData']): boolean => {
        return isBankDataTouched(data)
      },
      then: base(intl).fields.bankName.required(intl.formatMessage(messages.required))
    }),
    email: base(intl).fields.email.when('$bankData', {
      is: (data: INewPartnerStep2['bankData']): boolean => {
        return isBankDataTouched(data)
      },
      then: base(intl).fields.email.required(intl.formatMessage(messages.required))
    }),
    bankCoverLink: base(intl).fields.bankCoverLink,
    paymentType: base(intl).fields.paymentType.when('$bankData', {
      is: (data: INewPartnerStep2['bankData']): boolean => {
        return isBankDataTouched(data)
      },
      then: base(intl).fields.paymentType.required(intl.formatMessage(messages.required))
    }),
    clabeNumber: base(intl).fields.clabeNumber.when('$bankData', {
      is: (data: INewPartnerStep2['bankData']): boolean => {
        return isBankDataTouched(data)
      },
      then: base(intl).fields.clabeNumber.required(intl.formatMessage(messages.required))
    })
  }) as yup.SchemaOf<INewPartnerStep2['bankData']>
}

const required = (intl: IntlShape): yup.SchemaOf<INewPartnerStep2['bankData']> => {
  return yup.object().shape({
    bankName: yup.string().required(intl.formatMessage(messages.required)),
    email: base(intl).fields.email.required(intl.formatMessage(messages.required)),
    bankCoverLink: base(intl).fields.bankCoverLink,
    paymentType: base(intl).fields.paymentType.required(intl.formatMessage(messages.required)),
    clabeNumber: yup
      .string()
      .required(intl.formatMessage(messages.required))
      .length(18, intl.formatMessage(messages.clabeLength))
  })
}

export const bankDataSchema = {
  optional,
  required
}
